
import { z } from "zod";
import {
    zAbortResult as zAbortResultContract,
    zAlert as zAlertContract,
    zAlertRule as zAlertRuleContract,
    zAlertSink as zAlertSinkContract,
    zAuditChainVerification as zAuditChainVerificationContract,
    zAuditEvent as zAuditEventContract,
    zBackup as zBackupContract,
    zBackupCopy as zBackupCopyContract,
    zBackupCopyList as zBackupCopyListContract,
    zBackupTarget as zBackupTargetContract,
    zBucket as zBucketContract,
    zCluster as zClusterContract,
    zRollingUpdateState as zRollingUpdateStateContract,
    zClusterDiskList as zClusterDiskListContract,
    zClusterNode as zClusterNodeContract,
    zClusterNodeReinstatement as zClusterNodeReinstatementContract,
    zClusterNodeRevocation as zClusterNodeRevocationContract,
    zCredential as zCredentialContract,
    zCephCluster as zCephClusterContract,
    zCephHealthReport as zCephHealthReportContract,
    zCephPoolExplorerEntry as zCephPoolExplorerEntryContract,
    zCephPoolExplorerList as zCephPoolExplorerListContract,
    zCephPoolObjectsPage as zCephPoolObjectsPageContract,
    zCephCrushMap as zCephCrushMapContract,
    zCephOsdExplorerList as zCephOsdExplorerListContract,
    zDhcpService as zDhcpServiceContract,
    zDirectoryRealm as zDirectoryRealmContract,
    zFileShare as zFileShareContract,
    zFileShareSnapshot as zFileShareSnapshotContract,
    zFileShareLockdownResult as zFileShareLockdownResultContract,
    zGetSystemHealthResponse as zGetSystemHealthResponseContract,
    zGrant as zGrantContract,
    zHost as zHostContract,
    zEnclosure as zEnclosureContract,
    zEnclosureList as zEnclosureListContract,
    zIoServerPair as zIoServerPairContract,
    zIoServerPairCredential as zIoServerPairCredentialContract,
    zMultiscsiCluster as zMultiscsiClusterContract,
    zJoinToken as zJoinTokenContract,
    zMetricsToken as zMetricsTokenContract,
    zMonitoringOverview as zMonitoringOverviewContract,
    zMonitoringTrendSeries as zMonitoringTrendSeriesContract,
    zNetwork as zNetworkContract,
    zNetworkNodeBinding as zNetworkNodeBindingContract,
    zNodeDiskChunks as zNodeDiskChunksContract,
    zNodeRaid as zNodeRaidContract,
    zNodeVdisk as zNodeVdiskContract,
    zObjectStore as zObjectStoreContract,
    zPermissionInfo as zPermissionInfoContract,
    zRecoveryKeysExport as zRecoveryKeysExportContract,
    zReplicationLink as zReplicationLinkContract,
    zReplicationLinkDemoteResult as zReplicationLinkDemoteResultContract,
    zRole as zRoleContract,
    zS3Account as zS3AccountContract,
    zS3AccountSecret as zS3AccountSecretContract,
    zScope as zScopeContract,
    zScopeReport as zScopeReportContract,
    zSyncLink as zSyncLinkContract,
    zSyncLinkDemoteResult as zSyncLinkDemoteResultContract,
    zTarget as zTargetContract,
    zTask as zTaskContract,
    zUser as zUserContract,
    zVolume as zVolumeContract,
    zVolumeSnapshot as zVolumeSnapshotContract,
    zSnapshotRetention as zSnapshotRetentionContract,
    zCatalogCredentials as zCatalogCredentialsContract,
    zCatalogEntry as zCatalogEntryContract,
    zCatalogStatus as zCatalogStatusContract,
    zCatalogSummary as zCatalogSummaryContract,
    zClientAuditEvent as zClientAuditEventContract,
    zClientAuditTail as zClientAuditTailContract,
    zCatalogSource as zCatalogSourceContract,
    zNodeMount as zNodeMountContract,
    zNodeMountCensus as zNodeMountCensusContract,
} from "./generated/zod/zod.gen.js";



// FACADE-TIPATA (#183, ratifica 2026-08-19 §2 — 2026-08-21): the facade's
// response types, inferred from the generated zod schemas instead of the old
// `Record<string, unknown>` — so a contract drift dies at typecheck in every
// consumer instead of staying silent behind a webui narrowing cast.
//
// Reperti R1/R5 of #183, CLOSED by #195 (2026-08-21): the contract now
// declares `id` (and `scope`, where every render carries it) as
// response-required — the RenderedResource rule in contract/common.yaml — so
// the generated schemas demand them at runtime and the generated types say
// `id: string`. The 25 `zRenderedId` bridges this file used to re-strengthen
// at the validation boundary have DECAYED: the generated layer covers them.
//
// STILL OPTIONAL BY CONSTRUCTION: `zCluster` — the singleton's render OMITS
// `id` on purpose (M1 store row has no id column; the contract's optional id
// is wire-true there — components/backend/src/modules/cluster/model.ts) —
// and `Scope.scope`, absent exactly on the cluster root (no parent).

// ---- rendered flat resources: typed as generated ----

export const zUser = zUserContract;
export type User = z.infer<typeof zUser>;

export const zScope = zScopeContract;
export type Scope = z.infer<typeof zScope>;

// ENEA-B-R15: the report of a scope and its descendants (scopes.report).
export const zScopeReport = zScopeReportContract;
export type ScopeReport = z.infer<typeof zScopeReport>;

export const zRole = zRoleContract;
export type Role = z.infer<typeof zRole>;

export const zGrant = zGrantContract;
export type Grant = z.infer<typeof zGrant>;

export const zClusterNode = zClusterNodeContract;
export type ClusterNode = z.infer<typeof zClusterNode>;

// JBOD-DC-1 (2026-09-06): the fleet-wide disk census by WWID.
export const zClusterDiskList = zClusterDiskListContract;
export type ClusterDisk = z.infer<typeof zClusterDiskList>["items"][number];

export const zNodeRaid = zNodeRaidContract;
export type NodeRaid = z.infer<typeof zNodeRaid>;

export const zNodeVdisk = zNodeVdiskContract;
export type NodeVdisk = z.infer<typeof zNodeVdisk>;

export const zNetwork = zNetworkContract;
export type Network = z.infer<typeof zNetwork>;

// REMODEL R1 (#214, 2026-08-26): the Datapool resource is DEAD — the two
// honest cluster resources replace it (rename secco, no alias).
export const zCephCluster = zCephClusterContract;
export type CephCluster = z.infer<typeof zCephCluster>;
// JBOF-DC-1 (R25, 2026-10-25): the enclosure — dual-port NVMe shelf shared by N
// heads, one owner per disk (contract enclosures.yaml).
export const zEnclosure = zEnclosureContract;
export type Enclosure = z.infer<typeof zEnclosure>;
export const zEnclosureList = zEnclosureListContract;

export const zMultiscsiCluster = zMultiscsiClusterContract;
export type MultiscsiCluster = z.infer<typeof zMultiscsiCluster>;

// R4 (2026-08-26): l'esploratore ceph — le viste read-only del canale query.
export const zCephHealthReport = zCephHealthReportContract;
export type CephHealthReport = z.infer<typeof zCephHealthReport>;

export const zCephPoolExplorerEntry = zCephPoolExplorerEntryContract;
export type CephPoolExplorerEntry = z.infer<typeof zCephPoolExplorerEntry>;

export const zCephPoolExplorerList = zCephPoolExplorerListContract;
export type CephPoolExplorerList = z.infer<typeof zCephPoolExplorerList>;

export const zCephPoolObjectsPage = zCephPoolObjectsPageContract;
export type CephPoolObjectsPage = z.infer<typeof zCephPoolObjectsPage>;

export const zCephCrushMap = zCephCrushMapContract;
export type CephCrushMap = z.infer<typeof zCephCrushMap>;

export const zCephOsdExplorerList = zCephOsdExplorerListContract;
export type CephOsdExplorerList = z.infer<typeof zCephOsdExplorerList>;

export const zVolume = zVolumeContract;
export type Volume = z.infer<typeof zVolume>;

export const zVolumeSnapshot = zVolumeSnapshotContract;
export type VolumeSnapshot = z.infer<typeof zVolumeSnapshot>;

// ENEA-A-R5 (2026-10-23): la retention congelata, comune ai due snapshot.
export const zSnapshotRetention = zSnapshotRetentionContract;
export type SnapshotRetention = z.infer<typeof zSnapshotRetention>;

export const zHost = zHostContract;
export type Host = z.infer<typeof zHost>;

// ENEA HP-SAN-1 (R19): the I/O server pair (Lustre OSS/MDS) as a native rbd consumer.
export const zIoServerPair = zIoServerPairContract;
export type IoServerPair = z.infer<typeof zIoServerPair>;
export const zIoServerPairCredential = zIoServerPairCredentialContract;
export type IoServerPairCredential = z.infer<typeof zIoServerPairCredential>;

export const zTarget = zTargetContract;
export type Target = z.infer<typeof zTarget>;

export const zFileShare = zFileShareContract;
export type FileShare = z.infer<typeof zFileShare>;

// ENEA-A-R5 (2026-10-23): lo snapshot-risorsa di una share ceph.
export const zFileShareSnapshot = zFileShareSnapshotContract;
export type FileShareSnapshot = z.infer<typeof zFileShareSnapshot>;

// ENEA-A-R10 «catalogo» (2026-10-23): la voce del catalogo, lo stato, il
// riepilogo e la credenziale SQL in sola lettura.
export const zCatalogEntry = zCatalogEntryContract;
export type CatalogEntry = z.infer<typeof zCatalogEntry>;
export const zCatalogStatus = zCatalogStatusContract;
export type CatalogStatus = z.infer<typeof zCatalogStatus>;
export const zCatalogSummary = zCatalogSummaryContract;
export type CatalogSummary = z.infer<typeof zCatalogSummary>;
export const zCatalogCredentials = zCatalogCredentialsContract;
export type CatalogCredentials = z.infer<typeof zCatalogCredentials>;

// ENEA-A-R12 «gestione 2» (2026-10-23): l'evento di audit client (la forma
// unica per SMB/NFS/S3/WebDAV) e la coda `?tail=` di una share o di uno store.
export const zClientAuditEvent = zClientAuditEventContract;
export type ClientAuditEvent = z.infer<typeof zClientAuditEvent>;
export const zClientAuditTail = zClientAuditTailContract;
export type ClientAuditTail = z.infer<typeof zClientAuditTail>;

// ENEA HP-SAN-3 (R21, 2026-10-25): la sorgente «mount esterno» del catalogo —
// un filesystem gia' montato su un nodo, catalogato dal worker di QUEL nodo
// (catalogsources.yaml) — e il censimento dei mount di un nodo
// (`GET /cluster/nodes/{node}/mounts`), da cui il picker sceglie `mountPath`.
export const zCatalogSource = zCatalogSourceContract;
export type CatalogSource = z.infer<typeof zCatalogSource>;
export const zNodeMount = zNodeMountContract;
export type NodeMount = z.infer<typeof zNodeMount>;
export const zNodeMountCensus = zNodeMountCensusContract;
export type NodeMountCensus = z.infer<typeof zNodeMountCensus>;

export const zDhcpService = zDhcpServiceContract;
export type DhcpService = z.infer<typeof zDhcpService>;

export const zDirectoryRealm = zDirectoryRealmContract;
export type DirectoryRealm = z.infer<typeof zDirectoryRealm>;

export const zObjectStore = zObjectStoreContract;
export type ObjectStore = z.infer<typeof zObjectStore>;

export const zBucket = zBucketContract;
export type Bucket = z.infer<typeof zBucket>;

export const zS3Account = zS3AccountContract;
export type S3Account = z.infer<typeof zS3Account>;

export const zCredential = zCredentialContract;
export type Credential = z.infer<typeof zCredential>;

export const zBackupTarget = zBackupTargetContract;
export type BackupTarget = z.infer<typeof zBackupTarget>;

export const zBackup = zBackupContract;
export type Backup = z.infer<typeof zBackup>;

// SEC-WORM-1 (2026-09-06): the WORM copy of a backup.
export const zBackupCopy = zBackupCopyContract;
export type BackupCopy = z.infer<typeof zBackupCopy>;
export const zBackupCopyList = zBackupCopyListContract;

export const zReplicationLink = zReplicationLinkContract;
export type ReplicationLink = z.infer<typeof zReplicationLink>;

// #208 RESIDUI-REP (2026-08-23): the reversal's synchronous answer — what
// was demoted, what promotion memory was consumed (the link is gone).
export const zReplicationLinkDemoteResult = zReplicationLinkDemoteResultContract;
export type ReplicationLinkDemoteResult = z.infer<typeof zReplicationLinkDemoteResult>;

// #39 REP-SYNC-MS (2026-08-22): the site-B registration of a remote sync
// volume (contract synclinks.yaml).
export const zSyncLink = zSyncLinkContract;
export type SyncLink = z.infer<typeof zSyncLink>;

// #209 REP-S2S5 (2026-08-24): the sync reversal's synchronous answer — the
// consumed registration (the row is gone; the ReplicationLinkDemoteResult
// twin of the sync branch).
export const zSyncLinkDemoteResult = zSyncLinkDemoteResultContract;
export type SyncLinkDemoteResult = z.infer<typeof zSyncLinkDemoteResult>;

export const zAlertRule = zAlertRuleContract;
export type AlertRule = z.infer<typeof zAlertRule>;

export const zAlertSink = zAlertSinkContract;
export type AlertSink = z.infer<typeof zAlertSink>;



// ---- the singleton and the non-resource response shapes ----

export const zCluster = zClusterContract;
export type Cluster = z.infer<typeof zCluster>;

// ENEA-A-R16: the rolling update's persisted state (cluster.rollingUpdate.*).
export const zRollingUpdateState = zRollingUpdateStateContract;
export type RollingUpdateState = z.infer<typeof zRollingUpdateState>;

export const zAuditEvent = zAuditEventContract;
export type AuditEvent = z.infer<typeof zAuditEvent>;

// ENEA-A-R11 (2026-10-23, N4): the verdict of POST /cluster/audit/actions/verify
// — the per-node hash chain re-walked on the store (tamper EVIDENCE, not a
// signature: the manual says so).
export const zAuditChainVerification = zAuditChainVerificationContract;
export type AuditChainVerification = z.infer<typeof zAuditChainVerification>;

// ENEA-A-R11 (2026-10-23, N5): the read-only scrape token for GET /metrics —
// the secret is in the body ONCE (issue rotates; only its sha256 is kept).
export const zMetricsToken = zMetricsTokenContract;
export type MetricsToken = z.infer<typeof zMetricsToken>;

// Reperto R2 of #183, CLOSED by #195 (2026-08-21): `message` is declared by
// the contract now (monitoring.yaml Alert — producer renderAlert,
// components/backend/src/modules/monitoring/model.ts), so the wire-true
// bridge that re-added it after zod's unknown-key stripping has decayed —
// the generated schema carries the field.
export const zAlert = zAlertContract;
export type Alert = z.infer<typeof zAlert>;

export const zTask = zTaskContract;

export const zNodeDiskChunks = zNodeDiskChunksContract;
export type NodeDiskChunks = z.infer<typeof zNodeDiskChunks>;

export const zNetworkNodeBinding = zNetworkNodeBindingContract;
export type NetworkNodeBinding = z.infer<typeof zNetworkNodeBinding>;

export const zClusterNodeRevocation = zClusterNodeRevocationContract;
export type ClusterNodeRevocation = z.infer<typeof zClusterNodeRevocation>;

export const zClusterNodeReinstatement = zClusterNodeReinstatementContract;
export type ClusterNodeReinstatement = z.infer<typeof zClusterNodeReinstatement>;

export const zMonitoringOverview = zMonitoringOverviewContract;
export type MonitoringOverview = z.infer<typeof zMonitoringOverview>;

export const zMonitoringTrendSeries = zMonitoringTrendSeriesContract;
export type MonitoringTrendSeries = z.infer<typeof zMonitoringTrendSeries>;

export const zRecoveryKeysExport = zRecoveryKeysExportContract;
export type RecoveryKeysExport = z.infer<typeof zRecoveryKeysExport>;

export const zFileShareLockdownResult = zFileShareLockdownResultContract;
export type FileShareLockdownResult = z.infer<typeof zFileShareLockdownResult>;

export const zGetSystemHealthResponse = zGetSystemHealthResponseContract;
export type SystemHealth = z.infer<typeof zGetSystemHealthResponse>;

export type PermissionInfo = z.infer<typeof zPermissionInfoContract>;

export type AbortResult = z.infer<typeof zAbortResultContract>;



// ---- recomposed nested shapes ----

// The node pre-registration answer (#181 ondata 5): node + write-once join
// token. Recomposed here from the generated halves (the nested node requires
// its id per contract since #195).
export const zClusterNodeRegistration = z.object({
    node: zClusterNode,
    joinToken: zJoinTokenContract,
});
export type ClusterNodeRegistration = z.infer<typeof zClusterNodeRegistration>;

// The one-time S3 key-pair render (OBJ-1): the nested account is a rendered
// resource like any other — same required id, same runtime check.
export const zS3AccountWithSecret = z.object({
    account: zS3Account,
    secret: zS3AccountSecretContract,
});
